/**
 * FHIR CodeSystems, ValueSets, and Extensions URLs
 *
 * This file contains centralized constants for all FHIR system URLs used throughout the application.
 * Organizing these in one place ensures consistency and makes it easy to update URLs if needed.
 *
 * URL Structure:
 * - Standard FHIR: http://hl7.org/fhir/...
 * - HL7 Terminology: http://terminology.hl7.org/...
 * - US Core Extensions: http://hl7.org/fhir/us/core/StructureDefinition/...
 * - Custom (replace alopexcare.com): http://alopexcare.com/...
 */

/**
 * HL7 Standard CodeSystems
 * Official FHIR terminology systems maintained by HL7
 */
export const HL7_CODE_SYSTEMS = {
  /** Identifier types (MR, SS, DL, etc.) */
  IDENTIFIER_TYPE: 'http://terminology.hl7.org/CodeSystem/v2-0203',

  /** Marital status codes (M, S, D, W, etc.) */
  MARITAL_STATUS: 'http://terminology.hl7.org/CodeSystem/v3-MaritalStatus',

  /** Administrative gender (male, female, other, unknown) */
  ADMINISTRATIVE_GENDER: 'http://hl7.org/fhir/administrative-gender',

  /** Contact relationship (emergency, family, etc.) */
  CONTACT_RELATIONSHIP: 'http://terminology.hl7.org/CodeSystem/v2-0131',

  /** Address use (home, work, temp, old) */
  ADDRESS_USE: 'http://hl7.org/fhir/address-use',

  /** Contact point use (home, work, mobile) */
  CONTACT_POINT_USE: 'http://hl7.org/fhir/contact-point-use',
} as const;

/**
 * Standard FHIR Identifier Systems
 * Systems used for patient/practitioner identifiers
 */
export const FHIR_IDENTIFIER_SYSTEMS = {
  /** US National Provider Identifier (NPI) */
  US_NPI: 'http://hl7.org/fhir/sid/us-npi',

  /** NUCC Healthcare Provider Taxonomy */
  NUCC_TAXONOMY: 'http://nucc.org/provider-taxonomy',

  /** US Driver's License */
  US_DRIVERS_LICENSE: 'http://hl7.org/fhir/sid/us-dl',

  /** ICD-10-CM (Clinical Modification - US specific) */
  ICD_10: 'http://hl7.org/fhir/sid/icd-10-cm',

  /** SNOMED CT clinical terminology */
  SNOMED_CT: 'http://snomed.info/sct',

  /** LOINC (Logical Observation Identifiers Names and Codes) */
  LOINC: 'http://loinc.org',

  /** RxNorm (medication coding) */
  RXNORM: 'http://www.nlm.nih.gov/research/umls/rxnorm',
} as const;

/**
 * US Core FHIR Extensions
 * Extensions defined in the US Core Implementation Guide
 */
export const US_CORE_EXTENSIONS = {
  /** US Core Race Extension */
  RACE: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-race',

  /** US Core Ethnicity Extension */
  ETHNICITY: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity',

  /** US Core Birth Sex Extension */
  BIRTH_SEX: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex',

  /** US Core Tribal Affiliation Extension */
  TRIBAL_AFFILIATION: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-tribal-affiliation',

  /** US Core Individual Pronouns Extension */
  INDIVIDUAL_PRONOUNS:
    'http://hl7.org/fhir/us/core/StructureDefinition/us-core-individual-pronouns',
} as const;

/**
 * Time Entry Extensions
 * Extensions for tracking time entries on Task resources
 */
export const TIME_ENTRY_EXTENSIONS = {
  /** Duration in minutes for time entries */
  DURATION_MINUTES: 'http://alopexcare.com/fhir/StructureDefinition/time-entry-duration',

  /** Time entry category (administrative, clinical, training, etc.) */
  TIME_CATEGORY: 'http://alopexcare.com/fhir/StructureDefinition/time-entry-category',

  /** Whether time is billable */
  IS_BILLABLE: 'http://alopexcare.com/fhir/StructureDefinition/time-entry-billable',

  /** Associated project/activity reference */
  PROJECT_REFERENCE: 'http://alopexcare.com/fhir/StructureDefinition/time-entry-project',

  /** Notes/description for the time entry */
  NOTES: 'http://alopexcare.com/fhir/StructureDefinition/time-entry-notes',

  /** Role attribution (care-coordinator | pharmacist) on Time Entry and Daily Log Tasks */
  ROLE: 'http://alopexcare.com/fhir/StructureDefinition/time-entry-role',
} as const;

/**
 * Time Tracking Role CodeSystem + codes
 * Bound to the time-entry-role extension.
 */
export const TIME_TRACKING_ROLE_SYSTEM =
  'http://alopexcare.com/fhir/CodeSystem/time-tracking-role' as const;

export const TIME_TRACKING_ROLE_CODES = {
  CARE_COORDINATOR: 'care-coordinator',
  PHARMACIST: 'pharmacist',
} as const;

export type TimeTrackingRole =
  (typeof TIME_TRACKING_ROLE_CODES)[keyof typeof TIME_TRACKING_ROLE_CODES];

/**
 * Time-tracking output-type codes written by the log-time bot to Monthly/Daily Log Tasks.
 * The system URL is the existing time-tracking output-types URL (kept as-is for bot compatibility).
 */
export const TIME_TRACKING_OUTPUT_TYPE_CODES = {
  CC_TIME: 'cc-time',
  PHARMACIST_TIME: 'pharmacist-time',
} as const;

/**
 * Custom Organization Extensions
 * Application-specific extensions for data not covered by standard FHIR
 *
 * TODO: Replace 'alopexcare.com' with your actual organization's base URL
 * Example: 'https://fhir.mycompany.com/StructureDefinition/...'
 */
export const CUSTOM_EXTENSIONS = {
  /** Patient's religion */
  RELIGION: 'http://alopexcare.com/religion',

  /** Patient's timezone ID */
  TIMEZONE_ID: 'http://alopexcare.com/timezone-id',

  /** Associated pharmacy ID */
  PHARMACY_ID: 'http://alopexcare.com/pharmacy-id',

  /** Associated pharmacy email */
  PHARMACY_EMAIL: 'http://alopexcare.com/pharmacy-email',

  /** Referral source */
  REFERRAL_SOURCE: 'http://alopexcare.com/referral-source',

  /** Referral date */
  REFERRAL_DATE: 'http://alopexcare.com/referral-date',

  /** Practice name */
  PRACTICE_NAME: 'http://alopexcare.com/practice-name',

  /** Facility name */
  FACILITY: 'http://alopexcare.com/facility',

  /** MCHC (Managed Care Health Center) */
  MCHC: 'http://alopexcare.com/mchc',

  /** User status (active, inactive, locked) */
  USER_STATUS: 'http://alopexcare.com/user-status',

  /** Practitioner professional summary/bio */
  PROFESSIONAL_SUMMARY: 'http://alopexcare.com/professional-summary',

  /** Practitioner work experience (complex extension) */
  WORK_EXPERIENCE: 'http://alopexcare.com/work-experience',

  /** Practitioner professional awards (complex extension) */
  PROFESSIONAL_AWARD: 'http://alopexcare.com/professional-award',

  /** Practitioner professional skills */
  PROFESSIONAL_SKILLS: 'http://alopexcare.com/professional-skills',

  /** Medical group affiliation marker */
  MEDICAL_GROUP_AFFILIATION: 'http://alopexcare.com/medical-group-affiliation',

  /** Legacy entity ID for organization references during migration */
  LEGACY_ENTITY_ID: 'http://alopexcare.com/legacy-entity-id',

  /** Task defer/snooze metadata (deferCount, lastDeferReason, lastDeferredAt) */
  TASK_DEFER_METADATA: 'http://alopexcare.com/fhir/StructureDefinition/task-defer-metadata',

  /** Task urgency level (0-10 scale, separate from FHIR priority) */
  TASK_URGENCY: 'http://alopexcare.com/fhir/StructureDefinition/task-urgency',

  /** Task estimated duration */
  TASK_ESTIMATED_DURATION: 'http://alopexcare.com/fhir/StructureDefinition/task-estimated-duration',

  /** Task call center metadata (outcomeType, attemptNumber, outreachMethod, etc.) */
  TASK_CALL_METADATA: 'http://alopexcare.com/fhir/StructureDefinition/task-call-metadata',

  /** Task checklist (ordered list of checklist items with completion tracking) */
  TASK_CHECKLIST: 'http://alopexcare.com/fhir/StructureDefinition/task-checklist',

  /** Priority score (1-150 integer for fine-grained worklist ordering) */
  PRIORITY_SCORE: 'http://alopexcare.com/fhir/StructureDefinition/priority-score',

  /** Generation rationale (service + rationale for system-generated tasks) */
  GENERATION_RATIONALE: 'http://alopexcare.com/fhir/StructureDefinition/generation-rationale',

  /** Enrollment status on Patient */
  ENROLLMENT_STATUS: 'http://alopexcare.com/fhir/StructureDefinition/enrollment-status',

  /** Check severity for eligibility validation */
  CHECK_SEVERITY: 'http://alopexcare.com/fhir/StructureDefinition/check-severity',

  /** Check blocking flag for eligibility validation */
  CHECK_BLOCKING: 'http://alopexcare.com/fhir/StructureDefinition/check-blocking',

  /** Qualified ICD codes from eligibility check */
  QUALIFIED_ICD_CODES: 'http://alopexcare.com/fhir/StructureDefinition/qualified-icd-codes',

  /** Unqualified ICD codes from eligibility check */
  UNQUALIFIED_ICD_CODES: 'http://alopexcare.com/fhir/StructureDefinition/unqualified-icd-codes',

  /** Validation version for eligibility rules */
  VALIDATION_VERSION: 'http://alopexcare.com/fhir/StructureDefinition/validation-version',

  /** Qualification result reference */
  QUALIFICATION_RESULT: 'http://alopexcare.com/fhir/StructureDefinition/qualification-result',

  /** Review metadata */
  REVIEW_METADATA: 'http://alopexcare.com/fhir/StructureDefinition/review-metadata',

  /** Program ID extension */
  PROGRAM_ID: 'http://alopexcare.com/fhir/StructureDefinition/program-id',

  /** Contact notes on RelatedPerson */
  CONTACT_NOTES: 'http://alopexcare.com/fhir/StructureDefinition/contact-notes',

  /** Proxy relationship on consent performer */
  PROXY_RELATIONSHIP: 'http://alopexcare.com/fhir/StructureDefinition/proxy-relationship',

  /** Contact telecom extension */
  CONTACT_TELECOM: 'http://alopexcare.com/fhir/StructureDefinition/contact-telecom',

  /** Enabled programs on Organization */
  ENABLED_PROGRAMS: 'http://alopexcare.com/fhir/StructureDefinition/enabled-programs',

  /** Program code extension */
  PROGRAM_CODE: 'http://alopexcare.com/program-code',

  /** Status reason on EpisodeOfCare (why it's in current status) */
  STATUS_REASON: 'http://alopexcare.com/fhir/StructureDefinition/status-reason',

  /** Legacy referral ID for backward compatibility */
  LEGACY_REFERRAL_ID: 'http://alopexcare.com/legacy-referral-id',

  /** Last activity date on ServiceRequest (stamped on every referral save) */
  LAST_ACTIVITY_DATE: 'http://alopexcare.com/fhir/StructureDefinition/last-activity-date',

  /** Whether an AccessPolicy requires NPI for assigned users */
  REQUIRES_NPI: 'http://alopexcare.com/fhir/Extension/requires-npi',

  /** Patient communication preference (phone, email, text, mail) */
  COMMUNICATION_PREFERENCE:
    'http://alopexcare.com/fhir/StructureDefinition/communication-preference',

  /** Patient preferred call times */
  PREFERRED_CALL_TIMES: 'http://alopexcare.com/fhir/StructureDefinition/preferred-call-times',

  CHRONICITY: 'http://alopexcare.com/fhir/StructureDefinition/chronicity',

  /** Summary finding extension on Composition.section */
  SUMMARY_FINDING: 'http://alopexcare.com/fhir/StructureDefinition/summary-finding',

  /** Summary reporting period extension on Composition */
  SUMMARY_PERIOD: 'http://alopexcare.com/fhir/StructureDefinition/summary-period',

  /** Summary lifecycle status extension on Composition */
  SUMMARY_STATUS_EXT: 'http://alopexcare.com/fhir/StructureDefinition/summary-status',

  /** Summary approval metadata extension on Composition */
  SUMMARY_APPROVAL: 'http://alopexcare.com/fhir/StructureDefinition/summary-approval',

  /** AI generation metadata extension on Composition */
  AI_GENERATION_METADATA: 'http://alopexcare.com/fhir/StructureDefinition/ai-generation-metadata',
} as const;

/**
 * Organization Type Codes
 * Codes for Organization.type to identify organization category
 * Reference: http://terminology.hl7.org/CodeSystem/organization-type
 */
export const ORGANIZATION_TYPE_CODES = {
  /** Pharmacy */
  PHARMACY: 'prov',

  /** Healthcare Provider */
  HEALTHCARE_PROVIDER: 'prov',
} as const;

/**
 * Organization Type System URL
 * Standard HL7 CodeSystem for organization types
 */
export const ORGANIZATION_TYPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/organization-type';

/**
 * Custom Organization Identifier Systems
 * Custom systems for organization-specific identifiers
 */
export const CUSTOM_IDENTIFIER_SYSTEMS = {
  /** Organization's internal member ID */
  MEMBER_ID: 'http://alopexcare.com/member-id',

  /** Organization's internal medical record number */
  MRN: 'http://alopexcare.com/mrn',

  /** Organization's pharmacy identifier */
  PHARMACY_ID: 'http://alopexcare.com/pharmacy-id',

  /** Legacy user ID for backward compatibility */
  USER_ID: 'http://alopexcare.com/user-id',

  /** Legacy entity ID for organization references */
  ENTITY_ID: 'http://alopexcare.com/entity-id',

  /** Provider ID (legacy system identifier for backward compatibility) */
  PROVIDER_ID: 'http://alopexcare.com/provider-id',

  /** Bot identifier system */
  BOT_IDENTIFIER: 'http://alopexcare.com/fhir/Bot',

  /** Entity root marker for the root Organization in the entity hierarchy */
  ENTITY_ROOT: 'http://alopexcare.com/entity-root',
} as const;

/**
 * Custom CodeSystems
 * Organization-specific code systems for custom data
 */
export const CUSTOM_CODE_SYSTEMS = {
  /** Service request categories (enrollment, etc.) */
  SERVICE_CATEGORY: 'http://alopexcare.com/service-category',

  /** Care programs (CCM, RPM, RTM, BHI) */
  PROGRAMS: 'http://alopexcare.com/fhir/CodeSystem/program-types',

  /** Referral status codes */
  REFERRAL_STATUS: 'http://alopexcare.com/referral-status',

  /** Episode of care type (program enrollment type) */
  EPISODE_TYPE: 'http://alopexcare.com/fhir/CodeSystem/episode-type',

  /** User roles (admin, care-coordinator, provider, etc.) */
  USER_ROLES: 'http://alopexcare.com/fhir/CodeSystem/user-roles',

  /** Medical group affiliation types */
  AFFILIATION_TYPES: 'http://alopexcare.com/affiliation-types',

  /** Task types (ELIGIBILITY_REVIEW, CONSENT_CALL, etc.) */
  TASK_TYPES: 'http://alopexcare.com/fhir/CodeSystem/task-types',

  /** Call outcome types for task call center metadata */
  CALL_OUTCOME_TYPES: 'http://alopexcare.com/fhir/CodeSystem/call-outcome-types',

  /** Outreach methods (phone, email, in_person) */
  OUTREACH_METHODS: 'http://alopexcare.com/fhir/CodeSystem/outreach-methods',

  /** Languages */
  LANGUAGES: 'http://alopexcare.com/fhir/CodeSystem/languages',

  /** Religions */
  RELIGIONS: 'http://alopexcare.com/fhir/CodeSystem/religions',

  /** Eligibility status codes */
  ELIGIBILITY_STATUS: 'http://alopexcare.com/fhir/CodeSystem/eligibility-status-codes',

  /** Check severity codes */
  CHECK_SEVERITY: 'http://alopexcare.com/fhir/CodeSystem/check-severity-codes',

  /** Observation types */
  OBSERVATION_TYPES: 'http://alopexcare.com/CodeSystem/observation-types',

  /** Eligibility types */
  ELIGIBILITY_TYPES: 'http://alopexcare.com/fhir/CodeSystem/eligibility-types',

  /** Relationships */
  RELATIONSHIPS: 'http://alopexcare.com/fhir/CodeSystem/relationships',

  /** Consent types */
  CONSENT_TYPES: 'http://alopexcare.com/CodeSystem/consent-types',

  /** Consent parties */
  CONSENT_PARTIES: 'http://alopexcare.com/CodeSystem/consent-parties',

  /** Consent method */
  CONSENT_METHOD: 'http://alopexcare.com/CodeSystem/consent-method',

  /** Requested info categories */
  REQUESTED_INFO: 'http://alopexcare.com/CodeSystem/requested-info',

  /** Entity types for organizations */
  ENTITY_TYPES: 'http://alopexcare.com/fhir/CodeSystem/entity-types',

  /** Vital measurement methods */
  VITAL_METHODS: 'http://alopexcare.com/fhir/CodeSystem/vital-methods',

  /** Condition status codes */
  CONDITION_STATUS: 'http://alopexcare.com/CodeSystem/condition-status',

  /** Tracking categories */
  TRACKING_CATEGORIES: 'http://alopexcare.com/fhir/CodeSystem/tracking-categories',

  /** Tracking subcategories */
  TRACKING_SUBCATEGORIES: 'http://alopexcare.com/fhir/CodeSystem/tracking-subcategories',

  /** Flag source codes */
  FLAG_SOURCE: 'http://alopexcare.com/fhir/CodeSystem/flag-source-codes',

  /** App permissions */
  APP_PERMISSIONS: 'http://alopexcare.com/fhir/CodeSystem/app-permissions',

  /** Care team roles */
  CARE_TEAM_ROLES: 'http://alopexcare.com/fhir/CodeSystem/care-team-roles',

  /** Eligibility status (simplified) */
  ELIGIBILITY_STATUS_SIMPLE: 'http://alopexcare.com/CodeSystem/eligibility-status',

  /** Episode of care status reasons (enrollment pipeline cancellation/hold reasons) */
  EPISODE_STATUS_REASON: 'http://alopexcare.com/fhir/CodeSystem/episode-status-reason',

  /** Communication preference codes (phone, email, text, mail) */
  COMMUNICATION_PREFERENCE: 'http://alopexcare.com/fhir/CodeSystem/communication-preference',

  /** Problem categories for care plan conditions */
  PROBLEM_CATEGORY: 'http://alopexcare.com/fhir/CodeSystem/problem-category',

  /** Stedi payer directory (eligibility-enabled insurance providers) */
  STEDI_PAYERS: 'http://alopexcare.com/fhir/CodeSystem/stedi-payers',

  /** Provider roles within an organization (credential-based classification) */
  PROVIDER_ROLES: 'http://alopexcare.com/fhir/CodeSystem/provider-roles',

  /** Task type codes for care plan workflow tasks (action-card, evaluate-*, goal-gap, etc.) */
  TASK_TYPE: 'http://alopexcare.com/fhir/CodeSystem/task-type',

  /** Task category codes (quality-preventive, ccm-compliance, etc.) */
  TASK_CATEGORY: 'http://alopexcare.com/fhir/CodeSystem/task-category',

  /** Task input type codes (task-category discriminator) */
  TASK_INPUT_TYPE: 'http://alopexcare.com/fhir/CodeSystem/task-input-type',

  /** Task output type codes (completion-record) */
  TASK_OUTPUT_TYPE: 'http://alopexcare.com/fhir/CodeSystem/task-output-type',

  /** Document type codes (physician-care-summary, etc.) */
  DOCUMENT_TYPES: 'http://alopexcare.com/fhir/CodeSystem/document-types',

  /** Summary section type codes (urgent, significant, ongoing, care-team, additional-notes) */
  SUMMARY_SECTION_TYPE: 'http://alopexcare.com/fhir/CodeSystem/summary-section-type',

  /** Summary finding category codes */
  SUMMARY_FINDING_CATEGORY: 'http://alopexcare.com/fhir/CodeSystem/summary-finding-category',

  /** Summary trend status codes (stable, improving, unchanged, worsening) */
  SUMMARY_TREND_STATUS: 'http://alopexcare.com/fhir/CodeSystem/summary-trend-status',

  /** Summary lifecycle status codes (draft, in-review, approved, final, cancelled) */
  SUMMARY_STATUS: 'http://alopexcare.com/fhir/CodeSystem/summary-status',

  /** Caregiver relationship type (Spouse, Child, Parent, ..., Employer, State Agency, Federal Agency, Other) */
  CAREGIVER_RELATIONSHIP_TYPE: 'http://alopexcare.com/fhir/CodeSystem/caregiver-relationship-type',

  /** Care Team Communication reasons (Fall, Injury, Change in Condition, ...) */
  COMMUNICATION_REASONS: 'http://alopexcare.com/fhir/CodeSystem/communication-reasons',

  /** Communication medium — Alopex 'Other' extension code paired with v3-ParticipationMode */
  COMMUNICATION_MEDIUM_OTHER: 'http://alopexcare.com/fhir/CodeSystem/communication-medium-other',

  /** Contact disposition (outreach outcome) — shared by Encounter and Communication */
  CONTACT_DISPOSITION: 'http://alopexcare.com/fhir/CodeSystem/contact-disposition',

  /** Note category codes (SF1 migration categories + general-note for UI-created notes) */
  NOTE_CATEGORY: 'http://alopexcare.com/fhir/CodeSystem/note-category',
} as const;

/**
 * US Core CodeSystem URLs used in profiles
 */
export const US_CORE_CODE_SYSTEMS = {
  /** US Core DocumentReference category (clinical-note fixed slice) */
  DOC_REFERENCE_CATEGORY:
    'http://hl7.org/fhir/us/core/CodeSystem/us-core-documentreference-category',
} as const;

/** Extension URL stamped on each DocumentReference version snapshot to track the editor */
export const NOTE_LAST_EDITOR_EXT =
  'http://alopexcare.com/fhir/StructureDefinition/note-last-editor' as const;

/**
 * HL7 standard CodeSystems used outside HL7_CODE_SYSTEMS (additions)
 */
export const HL7_TERMINOLOGY = {
  /** v3-ParticipationMode — used for Communication.medium codes (PHONE, EMAILWRIT, FAXWRIT, FACE) */
  V3_PARTICIPATION_MODE: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode',
} as const;

/**
 * ValueSet URLs for the Care Team Communications surface
 */
export const CARE_TEAM_COMMUNICATION_VALUE_SETS = {
  /** Care Team Communication medium picklist (composite v3 + Alopex 'Other' with display overrides) */
  MEDIUM: 'http://alopexcare.com/fhir/ValueSet/care-team-communication-medium',

  /** Care Team Communication reason picklist */
  REASONS: 'http://alopexcare.com/fhir/ValueSet/communication-reasons',

  /** Outcome picklist subset of contact-disposition */
  OUTCOME: 'http://alopexcare.com/fhir/ValueSet/contact-disposition-care-team-communications',

  /** Family & Caregivers relationship picklist (binds Patient.contact.relationship on the Care Team page) */
  CAREGIVER_RELATIONSHIP_TYPE: 'http://alopexcare.com/fhir/ValueSet/caregiver-relationship-type',
} as const;

/**
 * Care Team Communications extension URLs
 */
export const CARE_TEAM_COMMUNICATION_EXTENSIONS = {
  /** Outcome of an outreach attempt (also used on Encounter per PatientContact.md) */
  CONTACT_DISPOSITION: 'http://alopexcare.com/fhir/StructureDefinition/contact-disposition',

  /** Contact point (phone / email / fax) used at time of communication; Type-driven UI label */
  COMMUNICATION_CONTACT_POINT:
    'http://alopexcare.com/fhir/StructureDefinition/communication-contact-point',

  /** Boolean flag indicating whether Communication.sent / .received carries a meaningful time component */
  COMMUNICATION_TIME_SPECIFIED:
    'http://alopexcare.com/fhir/StructureDefinition/communication-time-specified',

  /** Structured back-link from Communication to source care team participant */
  CARE_TEAM_MEMBER_LINK: 'http://alopexcare.com/fhir/StructureDefinition/care-team-member-link',

  /** Persisted on create by log-care-team-communication; never overwritten on update. */
  CREATED_AT: 'http://alopexcare.com/fhir/StructureDefinition/communication-created-at',

  /** Persisted on create by log-care-team-communication; never overwritten on update. */
  CREATED_BY: 'http://alopexcare.com/fhir/StructureDefinition/communication-created-by',
} as const;

/**
 * Canonical URL of the MedRec-create Task ActivityDefinition. Kept in sync
 * with `MEDREC_TEMPLATE_URL` in `apps/medplum/bots/shared/fhir-constants.ts`.
 */
export const MEDREC_TEMPLATE_URL =
  'http://alopexcare.com/fhir/ActivityDefinition/medrec-create' as const;

/**
 * Patient.contact role-flag extension URLs (Care Team Family & Caregivers)
 */
export const CAREGIVER_FLAG_EXTENSIONS = {
  /** Boolean flag marking the contact as the patient's Power of Attorney */
  POWER_OF_ATTORNEY: 'http://alopexcare.com/fhir/StructureDefinition/caregiver-power-of-attorney',

  /** Boolean flag marking the contact as an Emergency Contact */
  EMERGENCY_CONTACT: 'http://alopexcare.com/fhir/StructureDefinition/caregiver-emergency-contact',

  /** Boolean flag marking the contact as the patient's Next of Kin */
  NEXT_OF_KIN: 'http://alopexcare.com/fhir/StructureDefinition/caregiver-next-of-kin',
} as const;

/**
 * Identifier Type Codes
 * Common codes from http://terminology.hl7.org/CodeSystem/v2-0203
 */
export const IDENTIFIER_TYPE_CODES = {
  /** Medical Record Number */
  MR: 'MR',

  /** Driver's License Number */
  DL: 'DL',

  /** Member Number */
  MB: 'MB',

  /** Patient Internal Identifier */
  PI: 'PI',

  /** National Provider Identifier */
  NPI: 'NPI',
} as const;

/**
 * Marital Status Codes
 * Common codes from http://terminology.hl7.org/CodeSystem/v3-MaritalStatus
 */
export const MARITAL_STATUS_CODES = {
  /** Annulled */
  ANNULLED: 'A',

  /** Divorced */
  DIVORCED: 'D',

  /** Interlocutory */
  INTERLOCUTORY: 'I',

  /** Legally Separated */
  LEGALLY_SEPARATED: 'L',

  /** Married */
  MARRIED: 'M',

  /** Polygamous */
  POLYGAMOUS: 'P',

  /** Never Married / Single */
  NEVER_MARRIED: 'S',

  /** Domestic Partner */
  DOMESTIC_PARTNER: 'T',

  /** Unmarried */
  UNMARRIED: 'U',

  /** Widowed */
  WIDOWED: 'W',

  /** Unknown */
  UNKNOWN: 'UNK',
} as const;

/**
 * LOINC Vital Signs Codes
 * Standard LOINC codes for common vital signs observations
 * Reference: https://loinc.org/
 */
export const LOINC_VITAL_SIGNS = {
  /** Blood Pressure Panel - 85354-9 */
  BLOOD_PRESSURE_PANEL: '85354-9',

  /** Systolic Blood Pressure - 8480-6 */
  BLOOD_PRESSURE_SYSTOLIC: '8480-6',

  /** Diastolic Blood Pressure - 8462-4 */
  BLOOD_PRESSURE_DIASTOLIC: '8462-4',

  /** Heart Rate - 8867-4 */
  HEART_RATE: '8867-4',

  /** Body Temperature - 8310-5 */
  BODY_TEMPERATURE: '8310-5',

  /** Respiratory Rate - 9279-1 */
  RESPIRATORY_RATE: '9279-1',

  /** Oxygen Saturation - 2708-6 */
  OXYGEN_SATURATION: '2708-6',

  /** Body Weight - 29463-7 */
  BODY_WEIGHT: '29463-7',

  /** Body Height - 8302-2 */
  BODY_HEIGHT: '8302-2',

  /** Body Mass Index (BMI) - 39156-5 */
  BMI: '39156-5',

  /** Blood Glucose - 2339-0 */
  BLOOD_GLUCOSE: '2339-0',

  /** Head Circumference - 9843-4 */
  HEAD_CIRCUMFERENCE: '9843-4',
} as const;

/**
 * Vital Sign Units (UCUM - Unified Code for Units of Measure)
 */
export const VITAL_SIGN_UNITS = {
  /** Millimeters of Mercury (for blood pressure) */
  MM_HG: 'mm[Hg]',

  /** Beats per minute (for heart rate) */
  BPM: '/min',

  /** Degrees Fahrenheit */
  DEGREES_F: '[degF]',

  /** Degrees Celsius */
  DEGREES_C: 'Cel',

  /** Percentage (for oxygen saturation) */
  PERCENT: '%',

  /** Kilograms */
  KILOGRAMS: 'kg',

  /** Pounds */
  POUNDS: '[lb_av]',

  /** Centimeters */
  CENTIMETERS: 'cm',

  /** Inches */
  INCHES: '[in_i]',

  /** Milligrams per deciliter (for blood glucose) */
  MG_DL: 'mg/dL',

  /** Kilograms per square meter (for BMI) */
  KG_M2: 'kg/m2',

  /** Breaths per minute */
  BREATHS_PER_MIN: '/min',
} as const;

/**
 * Helper function to get identifier type coding
 */
export function getIdentifierTypeCoding(code: keyof typeof IDENTIFIER_TYPE_CODES) {
  return {
    system: HL7_CODE_SYSTEMS.IDENTIFIER_TYPE,
    code: IDENTIFIER_TYPE_CODES[code],
  };
}

/**
 * Helper function to get marital status coding
 */
export function getMaritalStatusCoding(code: keyof typeof MARITAL_STATUS_CODES) {
  return {
    system: HL7_CODE_SYSTEMS.MARITAL_STATUS,
    code: MARITAL_STATUS_CODES[code],
  };
}

// ============================================================================
// TASK STATUS AND PRIORITY MAPPINGS
// ============================================================================

/**
 * Legacy Task Status to FHIR Task.status mapping
 * Maps internal task statuses to standard FHIR Task status codes
 */
export const TASK_STATUS_TO_FHIR = {
  pending: 'requested',
  assigned: 'accepted',
  in_progress: 'in-progress',
  completed: 'completed',
  cancelled: 'cancelled',
  deferred: 'on-hold',
} as const;

/**
 * FHIR Task.status to Legacy Task Status mapping (reverse)
 */
export const FHIR_TO_TASK_STATUS = {
  requested: 'pending',
  received: 'pending',
  accepted: 'assigned',
  rejected: 'cancelled',
  ready: 'assigned',
  'in-progress': 'in_progress',
  'on-hold': 'deferred',
  completed: 'completed',
  cancelled: 'cancelled',
  'entered-in-error': 'cancelled',
  failed: 'cancelled',
  draft: 'pending',
} as const;

/**
 * Task Status type for legacy system
 */
export type LegacyTaskStatus = keyof typeof TASK_STATUS_TO_FHIR;

/**
 * FHIR Task Status type
 */
export type FhirTaskStatus = (typeof TASK_STATUS_TO_FHIR)[LegacyTaskStatus];

/**
 * Maps legacy priority level (0-10) to FHIR Task.priority
 */
export function mapPriorityToFhir(priorityLevel: number | undefined) {
  if (priorityLevel === undefined || priorityLevel === null) return 'routine';
  if (priorityLevel <= 2) return 'routine';
  if (priorityLevel <= 5) return 'urgent';
  if (priorityLevel <= 8) return 'asap';
  return 'stat';
}

/**
 * Maps FHIR Task.priority to legacy priority level (0-10)
 */
export function mapFhirToPriority(fhirPriority: string | undefined) {
  switch (fhirPriority) {
    case 'stat':
      return 10;
    case 'asap':
      return 7;
    case 'urgent':
      return 4;
    case 'routine':
    default:
      return 1;
  }
}

/**
 * Task type codes from our custom CodeSystem
 */
export const TASK_TYPE_CODES = {
  ELIGIBILITY_REVIEW: 'ELIGIBILITY_REVIEW',
  CONSENT_CALL: 'CONSENT_CALL',
  OUTREACH_CALL: 'OUTREACH_CALL',
  FOLLOW_UP_CALL: 'FOLLOW_UP_CALL',
  DOCUMENT_REVIEW: 'DOCUMENT_REVIEW',
  ENROLLMENT_COMPLETION: 'ENROLLMENT_COMPLETION',
  CARE_COORDINATION: 'CARE_COORDINATION',
  ASSESSMENT: 'ASSESSMENT',
  CARE_PLAN_REVIEW: 'CARE_PLAN_REVIEW',
  GENERAL: 'GENERAL',
  /** Time entry for tracking work duration */
  TIME_ENTRY: 'TIME_ENTRY',
} as const;

/**
 * Helper to create task type CodeableConcept
 */
export function getTaskTypeCoding(code: keyof typeof TASK_TYPE_CODES) {
  return {
    system: CUSTOM_CODE_SYSTEMS.TASK_TYPES,
    code: TASK_TYPE_CODES[code],
  };
}
